import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Download, Presentation, Sparkles, ChevronRight, ChevronLeft, FileText, Loader2, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const THEMES = [
  { id: 'midnight', label: 'Midnight', bg: '#0a0a1e', accent: '#6366f1' },
  { id: 'ocean',    label: 'Ocean',    bg: '#081e41', accent: '#00c8f0' },
  { id: 'forest',   label: 'Forest',   bg: '#0c2618', accent: '#34d399' },
  { id: 'ember',    label: 'Ember',    bg: '#230a0f', accent: '#fb6450' },
  { id: 'slate',    label: 'Slate',    bg: '#1e2330', accent: '#94a3b8' },
];

const TONES = [
  { id: 'professional', label: 'Professional' },
  { id: 'educational',  label: 'Educational'  },
  { id: 'casual',       label: 'Casual'       },
  { id: 'creative',     label: 'Creative'     },
  { id: 'sales',        label: 'Sales Pitch'  },
];

const SLIDE_COUNTS = [4, 6, 8, 10, 12, 15, 20];

// --- Mini slide renderer --------------------------------------------------
function SlidePreview({ slide, themeObj, index, isActive, onClick }) {
  const isTitle = slide.type === 'title';
  const isConclusion = slide.type === 'conclusion';

  return (
    <button
      onClick={onClick}
      data-testid={`slide-thumb-${index}`}
      className={`relative rounded-md overflow-hidden border-2 transition-all flex-shrink-0 cursor-pointer
        ${isActive ? 'border-white/50 shadow-lg shadow-black/40 scale-105' : 'border-white/10 hover:border-white/30'}`}
      style={{ width: 120, height: 68, background: themeObj.bg }}
    >
      {/* left accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: themeObj.accent }} />
      <div className="absolute inset-0 pl-3 pr-1.5 py-1.5 flex flex-col justify-center">
        <p className="text-white font-semibold leading-tight truncate" style={{ fontSize: 7 }}>
          {isTitle ? slide.title : slide.title}
        </p>
        {isTitle && slide.subtitle && (
          <p className="leading-tight truncate mt-0.5" style={{ fontSize: 5.5, color: themeObj.accent }}>
            {slide.subtitle}
          </p>
        )}
        {!isTitle && (slide.points || []).slice(0, 3).map((pt, i) => (
          <p key={i} className="leading-tight truncate" style={{ fontSize: 5, color: '#ccc', marginTop: 1 }}>
            ▸ {pt}
          </p>
        ))}
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: themeObj.accent }} />
      <span className="absolute top-0.5 right-1 text-white/40" style={{ fontSize: 4.5 }}>{index + 1}</span>
    </button>
  );
}

// --- Large slide view ----------------------------------------------------
function SlideLarge({ slide, themeObj, index, total }) {
  const isTitle = slide.type === 'title';

  return (
    <div
      data-testid="slide-large"
      className="relative rounded-xl overflow-hidden shadow-2xl"
      style={{ background: themeObj.bg, aspectRatio: '16/9', width: '100%', maxWidth: 760 }}
    >
      {/* left accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-3" style={{ background: themeObj.accent }} />
      {/* top band */}
      <div className="absolute top-0 left-0 right-0 h-10" style={{ background: `${themeObj.bg}dd` }} />
      {/* bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 h-2" style={{ background: themeObj.accent }} />

      <div className={`absolute inset-0 pl-8 pr-6 flex flex-col ${isTitle ? 'items-center justify-center text-center' : 'justify-start pt-12'}`}>
        {isTitle ? (
          <>
            <h1 className="font-bold text-white leading-tight mb-3" style={{ fontSize: 'clamp(18px,3.5vw,36px)' }}>{slide.title}</h1>
            {slide.subtitle && (
              <p className="italic" style={{ fontSize: 'clamp(11px,1.8vw,20px)', color: themeObj.accent }}>{slide.subtitle}</p>
            )}
          </>
        ) : (
          <>
            <h2 className="font-bold mb-4 leading-tight" style={{ fontSize: 'clamp(13px,2vw,22px)', color: slide.type === 'conclusion' ? themeObj.accent : '#fff' }}>
              {slide.title}
            </h2>
            <ul className="space-y-2">
              {(slide.points || []).map((pt, i) => (
                <li key={i} className="flex items-start gap-2" style={{ fontSize: 'clamp(9px,1.4vw,15px)' }}>
                  <span className="mt-0.5 flex-shrink-0" style={{ color: themeObj.accent }}>▸</span>
                  <span style={{ color: '#d0d5ff' }}>{pt}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* slide counter */}
      <span className="absolute bottom-3 right-4 text-white/30" style={{ fontSize: 10 }}>
        {index + 1} / {total}
      </span>
    </div>
  );
}

// --- Main page -----------------------------------------------------------
export default function PresentationPage() {
  const navigate = useNavigate();

  // Form state
  const [topic, setTopic] = useState('');
  const [nSlides, setNSlides] = useState(8);
  const [tone, setTone] = useState('professional');
  const [language, setLanguage] = useState('English');
  const [theme, setTheme] = useState('midnight');
  const [instructions, setInstructions] = useState('');

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [showNotes, setShowNotes] = useState(false);

  const themeObj = THEMES.find(t => t.id === theme) || THEMES[0];

  const handleGenerate = async () => {
    if (!topic.trim()) { toast.error('Enter a presentation topic'); return; }
    setGenerating(true);
    setResult(null);
    setActiveSlide(0);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/presentation/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ topic: topic.trim(), n_slides: nSlides, tone, language, theme, instructions: instructions.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generation failed');
      setResult(data);
      toast.success(`${data.total_slides}-slide presentation ready!`);
    } catch (e) {
      toast.error(e.message || 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!result?.download_url) return;
    const link = document.createElement('a');
    link.href = `${API}${result.download_url}`;
    link.download = result.filename || 'presentation.pptx';
    link.click();
    toast.success('Downloading PPTX...');
  };

  const handleRegenerate = () => {
    setResult(null);
    setActiveSlide(0);
    handleGenerate();
  };

  const slides = result?.slides || [];
  const currentSlide = slides[activeSlide];

  return (
    <div className="min-h-screen" style={{ background: '#0a0a18', fontFamily: 'Calibri, sans-serif' }}>
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between" style={{ background: '#0d0d24' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/ai-tools-dashboard')} className="text-white/50 hover:text-white transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#6366f1' }}>
              <Presentation size={16} className="text-white" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-sm">AI Presentation</h1>
              <p className="text-white/40 text-xs">Generate stunning PPTX slides</p>
            </div>
          </div>
        </div>

        {result && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleRegenerate}
              data-testid="regenerate-btn"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-white/70 hover:text-white border border-white/10 hover:border-white/30 text-sm transition-all"
            >
              <RefreshCw size={14} /> Regenerate
            </button>
            <button
              onClick={handleDownload}
              data-testid="download-pptx-btn"
              className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-white font-medium text-sm transition-all"
              style={{ background: '#6366f1' }}
            >
              <Download size={14} /> Download PPTX
            </button>
          </div>
        )}
      </div>

      <div className="flex h-[calc(100vh-61px)]">
        {/* Left sidebar — config */}
        <div className="w-72 border-r border-white/10 flex flex-col overflow-y-auto" style={{ background: '#0d0d24' }}>
          <div className="p-5 space-y-5 flex-1">
            {/* Topic */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-wider block mb-2">Presentation Topic *</label>
              <textarea
                data-testid="topic-input"
                value={topic}
                onChange={e => setTopic(e.target.value)}
                placeholder="e.g. The Future of Electric Vehicles"
                rows={3}
                className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/25 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
                style={{ background: '#ffffff08', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>

            {/* Slides count */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-wider block mb-2">Number of Slides</label>
              <div className="flex flex-wrap gap-1.5">
                {SLIDE_COUNTS.map(n => (
                  <button
                    key={n}
                    onClick={() => setNSlides(n)}
                    data-testid={`slides-count-${n}`}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${nSlides === n ? 'text-white' : 'text-white/40 hover:text-white/70'}`}
                    style={{ background: nSlides === n ? '#6366f1' : 'rgba(255,255,255,0.06)' }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Tone */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-wider block mb-2">Tone</label>
              <div className="space-y-1">
                {TONES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setTone(t.id)}
                    data-testid={`tone-${t.id}`}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${tone === t.id ? 'text-white font-medium' : 'text-white/40 hover:text-white/60'}`}
                    style={{ background: tone === t.id ? '#6366f120' : 'transparent', border: tone === t.id ? '1px solid #6366f150' : '1px solid transparent' }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-wider block mb-2">Color Theme</label>
              <div className="flex flex-wrap gap-2">
                {THEMES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t.id)}
                    data-testid={`theme-${t.id}`}
                    title={t.label}
                    className={`w-8 h-8 rounded-lg transition-all flex items-center justify-center ${theme === t.id ? 'ring-2 ring-white/60 scale-110' : 'hover:scale-105 ring-1 ring-white/10'}`}
                    style={{ background: t.bg }}
                  >
                    <div className="w-3 h-3 rounded-full" style={{ background: t.accent }} />
                  </button>
                ))}
              </div>
            </div>

            {/* Language */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-wider block mb-2">Language</label>
              <select
                value={language}
                onChange={e => setLanguage(e.target.value)}
                data-testid="language-select"
                className="w-full rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                style={{ background: '#ffffff08', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                {['English','Spanish','French','German','Portuguese','Italian','Japanese','Chinese','Arabic','Hindi'].map(l => (
                  <option key={l} value={l} style={{ background: '#1a1a3e' }}>{l}</option>
                ))}
              </select>
            </div>

            {/* Extra instructions */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-wider block mb-2">Extra Instructions <span className="normal-case text-white/25">(optional)</span></label>
              <textarea
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                placeholder="Focus on stats, include a Q&A slide..."
                rows={2}
                className="w-full rounded-lg px-3 py-2 text-xs text-white placeholder-white/20 resize-none focus:outline-none"
                style={{ background: '#ffffff06', border: '1px solid rgba(255,255,255,0.08)' }}
              />
            </div>
          </div>

          {/* Generate button */}
          <div className="p-5 border-t border-white/10">
            <button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-btn"
              className="w-full py-3 rounded-xl font-semibold text-white text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-60"
              style={{ background: generating ? '#4f46e5' : 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}
            >
              {generating ? (
                <><Loader2 size={16} className="animate-spin" /> Generating slides...</>
              ) : (
                <><Sparkles size={16} /> {result ? 'Regenerate' : 'Generate Presentation'}</>
              )}
            </button>
          </div>
        </div>

        {/* Main area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!result && !generating ? (
            /* Empty state */
            <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: '#6366f115', border: '1px solid #6366f130' }}>
                <Presentation size={36} style={{ color: '#6366f1' }} />
              </div>
              <div className="text-center">
                <h2 className="text-white text-xl font-semibold mb-2">Create Your Presentation</h2>
                <p className="text-white/40 text-sm max-w-sm">Enter a topic on the left and click Generate to build a full slide deck with titles, content and speaker notes.</p>
              </div>
              <div className="grid grid-cols-3 gap-3 max-w-lg w-full">
                {['Product Launch 2026','Climate Change Solutions','Intro to Machine Learning'].map(ex => (
                  <button
                    key={ex}
                    onClick={() => setTopic(ex)}
                    className="p-3 rounded-xl text-left text-xs text-white/60 hover:text-white transition-all"
                    style={{ background: '#ffffff06', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <FileText size={12} className="mb-1.5 opacity-50" />
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          ) : generating ? (
            /* Loading state */
            <div className="flex-1 flex flex-col items-center justify-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: '#6366f115' }}>
                  <Loader2 size={28} className="animate-spin" style={{ color: '#6366f1' }} />
                </div>
              </div>
              <p className="text-white font-medium">Building your presentation...</p>
              <p className="text-white/40 text-sm">AI is creating {nSlides} slides • This takes ~15 seconds</p>
            </div>
          ) : (
            /* Slide preview */
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Main slide view */}
              <div className="flex-1 flex items-center justify-center p-6 overflow-hidden">
                <div className="w-full max-w-3xl">
                  {currentSlide && (
                    <SlideLarge
                      slide={currentSlide}
                      themeObj={themeObj}
                      index={activeSlide}
                      total={slides.length}
                    />
                  )}

                  {/* Speaker notes toggle */}
                  {currentSlide?.speaker_notes && (
                    <div className="mt-3">
                      <button
                        onClick={() => setShowNotes(!showNotes)}
                        className="text-xs text-white/40 hover:text-white/70 flex items-center gap-1 transition-colors"
                      >
                        <FileText size={12} />
                        {showNotes ? 'Hide' : 'Show'} speaker notes
                      </button>
                      {showNotes && (
                        <div className="mt-2 p-3 rounded-lg text-xs text-white/60 leading-relaxed"
                          style={{ background: '#ffffff08', border: '1px solid rgba(255,255,255,0.08)' }}>
                          {currentSlide.speaker_notes}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Prev / Next */}
                  <div className="flex items-center justify-between mt-4">
                    <button
                      onClick={() => setActiveSlide(s => Math.max(0, s - 1))}
                      disabled={activeSlide === 0}
                      data-testid="prev-slide-btn"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-white/50 hover:text-white disabled:opacity-20 transition-all"
                      style={{ background: '#ffffff08' }}
                    >
                      <ChevronLeft size={16} /> Prev
                    </button>
                    <span className="text-white/40 text-sm">{activeSlide + 1} of {slides.length}</span>
                    <button
                      onClick={() => setActiveSlide(s => Math.min(slides.length - 1, s + 1))}
                      disabled={activeSlide === slides.length - 1}
                      data-testid="next-slide-btn"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-white/50 hover:text-white disabled:opacity-20 transition-all"
                      style={{ background: '#ffffff08' }}
                    >
                      Next <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Thumbnail strip */}
              <div className="border-t border-white/10 px-4 py-3 flex gap-2.5 overflow-x-auto" style={{ background: '#0d0d24' }}>
                {slides.map((slide, i) => (
                  <SlidePreview
                    key={i}
                    slide={slide}
                    themeObj={themeObj}
                    index={i}
                    isActive={i === activeSlide}
                    onClick={() => setActiveSlide(i)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
